import { useRef, useState } from 'react';
import type { RoomField } from './types';

/**
 * The file input — upload, show what's attached, and replace it.
 *
 * Distinct from the scalar FieldInput because a file is a whole async flow (pick → upload with
 * progress → confirmed), not a value that changes on keystroke. The parent owns the network + the
 * answer state via `onUpload`; this component owns only the local UI (picking, the progress bar, an
 * upload error).
 *
 * No standalone "remove" — the backend has no clear-file endpoint yet (Phase 6 ships upload +
 * replace). Picking a new file replaces the old one, which is the common "I attached the wrong
 * scan" case; a true remove is a small follow-up.
 */
export function FileField({
  field,
  file,
  onUpload,
}: {
  field: RoomField;
  file: { filename: string; sizeBytes: number; mime: string } | null;
  /** Runs the 3-step upload; `onProgress` feeds this component's bar (0–100). */
  onUpload: (file: File, onProgress: (pct: number) => void) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    // Reset the input so picking the SAME filename again still fires onChange.
    e.target.value = '';
    if (!chosen || busy) return;

    setBusy(true);
    setError(null);
    setProgress(0);
    try {
      await onUpload(chosen, setProgress);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ההעלאה נכשלה. נסו שוב.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="room-file">
      {file && !busy && (
        <div className="room-file-current">
          <span className="room-file-name">
            <bdi>{file.filename}</bdi>
          </span>
          <span className="room-file-size">{formatBytes(file.sizeBytes)}</span>
        </div>
      )}

      {busy && (
        <div className="room-file-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <div className="room-file-progress-bar" style={{ inlineSize: `${progress}%` }} />
          <span className="room-file-progress-label">מעלה… {progress}%</span>
        </div>
      )}

      {error && (
        <p className="room-error" role="alert">
          {error}
        </p>
      )}

      {!busy && (
        <>
          {/* Visually hidden; the styled button below triggers it. Accepted mime is server-enforced. */}
          <input
            ref={inputRef}
            id={`room-field-${field.id}`}
            type="file"
            className="room-file-input"
            onChange={pick}
          />
          <button type="button" className="btn-ghost" onClick={() => inputRef.current?.click()}>
            {file ? 'החלפת הקובץ' : 'בחירת קובץ'}
          </button>
        </>
      )}
    </div>
  );
}

/** Human-readable size — the client just needs a rough sense their file went up. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
